import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getFilterStateFromURL,
  saveFilterStateToURL,
  clearFilterStateFromURL,
  DEFAULT_PAGE_SIZE_OPTIONS
} from 'common/table/utils/tableV3Storage';
import { hasTableV3Filters } from 'common/table/utils/hasTableV3Filters';
import 'common/table/styles/table-v3.css';
import { mapColumns } from 'common/table/utils/mapTableColumns';
import {
  TableV3Data,
  TableV3ColDefs,
  TableV3Sort,
  TableV3SearchColumn,
  TableV3FilterColumn,
  TableV3PaginationOptions,
  TableV3FilterItem,
  TableV3SelectionMode
} from 'common/table/types/TableV3';
import { Chip, Stack, Box, TablePagination, SxProps } from '@mui/material';
import { Button } from 'system/atoms';
import {
  TableV3AgGrid,
  TableV3AgGridRef
} from 'common/table/components/TableV3AgGrid';

const filterActionButtonSx: SxProps = {
  textDecoration: 'underline',
  fontWeight: '500',
  fontSize: '14px'
};

export type TableV3Filters<T> = {
  searchFields?: {
    [key in keyof T]: string;
  };
  filterValues?: {
    [key in keyof T]: TableV3FilterItem[];
  };
};

type TableSelectionOptions<T> = {
  mode: TableV3SelectionMode;
  identifier: keyof T;
  onSelectionChanged: (selectedIds: string[], selectedRows: T[]) => void;
  isRowSelectable?: (row: T) => boolean;
};

type TableProps<T, K> = {
  tableId: string;
  columns: TableV3ColDefs<T, K>;
  data: TableV3Data<T>;
  defaultSort: TableV3Sort<K> | 'unset';
  selectionOptions?: TableSelectionOptions<T>;
  loading?: boolean;
  columnsCacheKey?: string; // Cache key - when this changes, columns are considered updated
  emptyStateComponent?: React.ReactNode;
  // Opt-in: persist this table's column layout (order, width, pinning, sort)
  // to localStorage. Visibility is not persisted: each page derives it from its
  // column definitions. Off by default — only set when the table is
  // intentionally configured for per-user persistence.
  persistColumnState?: boolean;
  // For pages that render their own filter summary and would otherwise show two.
  hideFilterSummary?: boolean;
  // Old column key -> current one, applied to filters restored from the URL so
  // a link shared before a column was renamed still filters.
  filterKeyAliases?: Record<string, string>;
  onSort?: (sort: TableV3Sort<K>) => void;
  onFiltersChanged?: (newFilters: TableV3Filters<T>) => void;
  paginationOptions?: TableV3PaginationOptions;
};

export type TableV3Ref<T> = {
  resetTable: () => void;
  deselectAll: () => void;
  select: (ids: string[]) => void;
  setColumnsVisible: (keys: string[], visible: boolean) => void;
  refreshColumns: (keys: string[]) => void;
  clearFilters: () => void;
  applyFilters: (filters: TableV3Filters<T>) => void;
};

/**
 * A table component.
 * @param {object} props
 * @property tableId - The id of the table. This is used to store the table state in localStorage.
 * @property columns - An array of objects with name and key properties
 * @property data - An array of row objects
 * @property defaultSort - The default sort of the table, unset is used to indicate that no default sort should be applied, this is required as it used to be forgotten.
 * @property selectionOptions - The selection options of the table
 */
const TableV3Inner = <T, K>(
  {
    tableId,
    columns,
    data,
    defaultSort,
    selectionOptions,
    loading = false,
    columnsCacheKey,
    emptyStateComponent,
    persistColumnState = false,
    hideFilterSummary = false,
    filterKeyAliases,
    onSort,
    onFiltersChanged,
    paginationOptions
  }: TableProps<T, K>,
  ref: React.Ref<TableV3Ref<T>>
): React.ReactElement => {
  const selectionOptionsRef = useRef(selectionOptions);
  selectionOptionsRef.current = selectionOptions;

  // Stabilize these values so they don't cause re-renders
  const stableRowSelection = useRef(selectionOptions?.mode);
  const stableRowIdentifier = useRef(selectionOptions?.identifier);

  const agGridRef = useRef<TableV3AgGridRef>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    resetTable: () => agGridRef.current?.resetTable(),
    deselectAll: () => agGridRef.current?.deselectAll(),
    select: (ids: string[]) => agGridRef.current?.select(ids),
    setColumnsVisible: (keys: string[], visible: boolean) =>
      agGridRef.current?.setColumnsVisible(keys, visible),
    refreshColumns: (keys: string[]) => agGridRef.current?.refreshColumns(keys),
    clearFilters: () => {
      clearAllFilters();
    },
    applyFilters: (filters: TableV3Filters<T>) => {
      const newSearchFields = filters.searchFields;
      const newFilterValues = filters.filterValues;
      setSearchFields(newSearchFields || ({} as TableV3SearchColumn<T>));
      setFilterValues(newFilterValues || ({} as TableV3FilterColumn<T>));
      onFiltersChanged?.({
        searchFields: newSearchFields,
        filterValues: newFilterValues
      });
    }
  }));

  const clearAllFilters = useCallback(() => {
    setSearchFields({} as TableV3SearchColumn<T>);
    setFilterValues({} as TableV3FilterColumn<T>);
    onFiltersChanged?.({
      searchFields: {} as TableV3SearchColumn<T>,
      filterValues: {} as TableV3FilterColumn<T>
    });
  }, [onFiltersChanged]);

  // Load filters from URL params during initialization
  const initialFilters = useMemo(() => {
    const restored = getFilterStateFromURL<T>(searchParams, tableId);
    if (!restored || !filterKeyAliases) return restored;

    const filterValues = Object.fromEntries(
      Object.entries(restored.filterValues ?? {}).map(([key, value]) => [
        filterKeyAliases[key] ?? key,
        value
      ])
    ) as typeof restored.filterValues;

    return { ...restored, filterValues };
  }, []);

  const [searchFields, setSearchFields] = useState<TableV3SearchColumn<T>>(
    () => initialFilters?.searchFields || ({} as TableV3SearchColumn<T>)
  );
  const [filterValues, setFilterValues] = useState<TableV3FilterColumn<T>>(
    () => initialFilters?.filterValues || ({} as TableV3FilterColumn<T>)
  );

  // Notify parent of initial filters after mount to ensure parent's state is ready
  const hasNotifiedInitialFilters = useRef(false);
  useEffect(() => {
    if (!hasNotifiedInitialFilters.current) {
      onFiltersChanged?.({
        searchFields: searchFields,
        filterValues: filterValues
      });
      hasNotifiedInitialFilters.current = true;
    }
  }, []);

  // Save filters to URL params when they change
  useEffect(() => {
    const hasFilters =
      Object.keys(searchFields).length > 0 ||
      Object.keys(filterValues).length > 0;

    if (hasFilters) {
      saveFilterStateToURL<T>(
        searchParams,
        setSearchParams,
        {
          searchFields,
          filterValues
        },
        tableId
      );
    } else {
      clearFilterStateFromURL(searchParams, setSearchParams, tableId);
    }
  }, [searchFields, filterValues]);

  const removeFilterValue = useCallback(
    (column: keyof T, oldValue: string) => {
      setFilterValues((prevFilterValues) => {
        const currentFilterValues = prevFilterValues[column] ?? [];
        const newValue = currentFilterValues.filter(
          (item) => item.value !== oldValue
        );
        const newFilterValues = { ...prevFilterValues, [column]: newValue };
        if (newValue.length === 0) {
          delete newFilterValues[column];
        }

        setSearchFields((prevSearchFields) => {
          onFiltersChanged?.({
            searchFields: prevSearchFields,
            filterValues: newFilterValues
          });
          return prevSearchFields;
        });

        return newFilterValues;
      });
    },
    [onFiltersChanged]
  );

  const updateFilterValues = useCallback(
    ({ column, item }: { column: keyof T; item: TableV3FilterItem }) => {
      setFilterValues((prevFilterValues) => {
        const currentFilterValues = prevFilterValues[column] ?? [];

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const columnDef = columns.find((col) => col.field === (column as any));
        const isMultipleSelection =
          columnDef?.filterOptions?.selectionType === 'multiple' ||
          columnDef?.serverFilterOptions?.selectionType === 'multiple';

        // If value already exists, remove it
        if (currentFilterValues.some((i) => i.value === item.value)) {
          const newValue = currentFilterValues.filter(
            (i) => i.value !== item.value
          );
          const newFilterValues = { ...prevFilterValues, [column]: newValue };
          if (newValue.length === 0) {
            delete newFilterValues[column];
          }

          setSearchFields((prevSearchFields) => {
            onFiltersChanged?.({
              searchFields: prevSearchFields,
              filterValues: newFilterValues
            });
            return prevSearchFields;
          });

          return newFilterValues;
        }

        let newValue: TableV3FilterItem[];
        if (isMultipleSelection) {
          newValue = [...currentFilterValues, item];
        } else {
          newValue = [item];
        }

        const newFilterValues = { ...prevFilterValues, [column]: newValue };

        setSearchFields((prevSearchFields) => {
          onFiltersChanged?.({
            searchFields: prevSearchFields,
            filterValues: newFilterValues
          });
          return prevSearchFields;
        });

        return newFilterValues;
      });
    },
    [columns, onFiltersChanged]
  );

  const replaceFilterValues = useCallback(
    ({ column, items }: { column: keyof T; items: TableV3FilterItem[] }) => {
      setFilterValues((prevFilterValues) => {
        const newFilterValues = { ...prevFilterValues, [column]: items };
        if (items.length === 0) {
          delete newFilterValues[column];
        }

        setSearchFields((prevSearchFields) => {
          onFiltersChanged?.({
            searchFields: prevSearchFields,
            filterValues: newFilterValues
          });
          return prevSearchFields;
        });

        return newFilterValues;
      });
    },
    [onFiltersChanged]
  );

  const handleSearch = useCallback(
    ({ column, value }: { column: keyof T; value: string }) => {
      setSearchFields((prevSearchFields) => {
        const newSearchFields = { ...prevSearchFields, [column]: value };

        setFilterValues((prevFilterValues) => {
          onFiltersChanged?.({
            searchFields: newSearchFields,
            filterValues: prevFilterValues
          });
          return prevFilterValues;
        });

        return newSearchFields;
      });
    },
    [onFiltersChanged]
  );

  const memoizedColumnDefs = useMemo(() => {
    return mapColumns({
      columns,
      onSearch: handleSearch,
      onFilterSelect: updateFilterValues,
      onFilterApply: replaceFilterValues
    });
  }, [columns, handleSearch, updateFilterValues, replaceFilterValues]);

  const memoizedContext = useMemo(
    () => ({
      filterValues: Object.fromEntries(
        Object.entries(filterValues).map(([key, items]) => [
          key,
          (items as TableV3FilterItem[]).map((i) => i.value)
        ])
      ) as Record<string, string[]>,
      filterItems: filterValues as Record<string, TableV3FilterItem[]>,
      updateFilterValues
    }),
    [filterValues, updateFilterValues]
  );

  // Create a stable callback that always uses the latest selectionOptions from the ref
  const handleSelectionChanged = useCallback(
    (selectedIds: string[], selectedRows: T[]) => {
      const currentSelectionOptions = selectionOptionsRef.current;
      if (!currentSelectionOptions) return;
      currentSelectionOptions.onSelectionChanged?.(selectedIds, selectedRows);
    },
    [] // Empty deps - always uses ref for latest callback
  );

  const hasFiltersApplied = hasTableV3Filters({ searchFields, filterValues });

  // Pagination handlers - server-side only
  const handleChangePage = useCallback(
    (_event: unknown, newPage: number) => {
      paginationOptions?.onChangePage(newPage);
      window.scrollTo({ top: 200, behavior: 'auto' });
    },
    [paginationOptions]
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      paginationOptions?.onChangePageSize?.(newRowsPerPage);
      window.scrollTo({ top: 200, behavior: 'auto' });
    },
    [paginationOptions]
  );

  const columnFields = columns.map((col) => col.field);

  // Clear data when loading to prevent cached data from showing behind loading overlay
  const displayData = loading ? [] : data;

  // `defaultSort` names a sortField; the grid matches on column id. `onSort`
  // translates the other way, so translate here too.
  // Keyed on primitives: neither `columns` nor `defaultSort` has a stable
  // identity, and the grid below is memoised on the result.
  const sortFieldKey = defaultSort === 'unset' ? null : defaultSort.field;
  const sortDirection = defaultSort === 'unset' ? null : defaultSort.direction;
  const sortFieldsKey = JSON.stringify(
    columns.map((col) => [col.field, col.sortField])
  );

  const mappedDefaultSort = useMemo(() => {
    if (sortFieldKey === null || sortDirection === null)
      return 'unset' as const;

    const columnDef = columns.find((col) => col.sortField === sortFieldKey);

    // Cast: `colId` is a field path, not `keyof T`, in ag-grid's typing.
    return columnDef
      ? ({
          field: columnDef.field,
          direction: sortDirection
        } as unknown as TableV3Sort<T>)
      : ('unset' as const);
  }, [sortFieldKey, sortDirection, sortFieldsKey]);

  const memoizedTableV3AgGrid = useMemo(() => {
    return (
      <TableV3AgGrid
        ref={agGridRef}
        tableId={tableId}
        rowSelection={stableRowSelection.current}
        columnDefs={memoizedColumnDefs}
        rowData={displayData}
        context={memoizedContext}
        loading={loading}
        defaultSort={mappedDefaultSort}
        columnsCacheKey={columnsCacheKey}
        emptyStateComponent={emptyStateComponent}
        isFiltering={hasFiltersApplied}
        persistColumnState={persistColumnState}
        onSort={(sort: TableV3Sort<T>) => {
          const field = sort.field;
          const columnDef = columns.find((col) => col.field === field);
          if (!columnDef?.sortField) return;
          onSort?.({
            field: columnDef.sortField,
            direction: sort.direction
          });
        }}
        rowIdentifier={stableRowIdentifier.current}
        onSelectionChanged={handleSelectionChanged}
        isRowSelectable={selectionOptions?.isRowSelectable}
      />
    );
  }, [
    tableId,
    JSON.stringify(columnFields),
    JSON.stringify(displayData),
    loading,
    persistColumnState,
    mappedDefaultSort
  ]);

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {!hideFilterSummary && (
        <Stack direction="row" spacing={2} flexWrap="wrap">
          {Object.entries(searchFields).map(([column, value]) => (
            <Chip
              variant="outlined"
              key={`search-${column}`}
              label={`"${value}"`}
              onDelete={() => {
                const newSearchFields = { ...searchFields };
                delete newSearchFields[column as keyof T];
                setSearchFields(newSearchFields);
                onFiltersChanged?.({
                  searchFields: newSearchFields,
                  filterValues
                });
              }}
            />
          ))}
          {Object.entries(filterValues).map(([column, filter]) => (
            <Fragment key={`filter-${column}`}>
              {(filter as TableV3FilterItem[]).map((filter) => (
                <Chip
                  variant="outlined"
                  key={`filter-${column}-${filter.value}`}
                  label={`"${filter.label}"`}
                  onDelete={() => {
                    removeFilterValue(column as keyof T, filter.value);
                  }}
                />
              ))}
            </Fragment>
          ))}
          {hasFiltersApplied && (
            <Button
              variant="tertiary"
              onClick={() => {
                clearAllFilters();
              }}
              sx={filterActionButtonSx}
            >
              Clear all filters
            </Button>
          )}
          <Button
            variant="tertiary"
            onClick={() => {
              agGridRef.current?.resetTable();
            }}
            sx={filterActionButtonSx}
          >
            Reset table
          </Button>
        </Stack>
      )}
      <div
        className={selectionOptions?.mode ? 'table-with-selection' : ''}
        style={{
          height: 'auto',
          minHeight: '400px',
          width: '100%',
          position: 'relative'
        }}
      >
        {memoizedTableV3AgGrid}
      </div>
      {paginationOptions && paginationOptions.totalRows > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <TablePagination
            component="div"
            count={paginationOptions.totalRows}
            page={paginationOptions.currentPage}
            rowsPerPage={paginationOptions.pageSize}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={
              paginationOptions.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS
            }
          />
        </Box>
      )}
    </Stack>
  );
};

// Need to use a type assertion to preserve generic types with forwardRef
export const TableV3 = forwardRef(TableV3Inner) as <T, K>(
  props: TableProps<T, K> & { ref?: React.Ref<TableV3Ref<T>> }
) => React.ReactElement;
